import { Helmet } from 'react-helmet-async';

import {
  Content,
  Header,
  Heading,
  Main,
  SidebarTrigger,
  Stack,
  Text,
  useSidebar,
} from '@eluve/components';

import { SettingsBackLink } from './SettingsBackLink';

/**
 * Main-area frame for a settings page: while the sidebar presents as a
 * sheet, a compact header bar hosts the sidebar trigger and the page title;
 * with the sidebar pinned, the title renders as a large in-content heading.
 * The title (falling back to `parentTitle`) also becomes the document
 * title, keeping the browser tab in sync with the page.
 *
 * `actions` render right-aligned on the title row (e.g. a table page's
 * primary CTA) and stay visible while the large title yields to the header
 * bar. `description` renders as supporting copy beneath the title row.
 *
 * `parentTitle` marks the page as a drill-in: the header bar shows the
 * parent page's title, while the page title renders as a smaller,
 * always-visible heading. `backTo` opens the content with a back link; omit
 * it for pages that bring their own back affordance.
 *
 * `size="wide"` renders a full-width pane for pages that bring their own
 * content layout (tables, trees, detail pages). `padded={false}` drops the
 * frame's content padding for pages that own their full layout.
 *
 * Must be rendered inside a `Layout`, as a sibling of the page's `Sidebar`.
 */
export const SettingsPageFrame = ({
  title,
  parentTitle,
  backTo,
  description,
  actions,
  size = 'column',
  padded = true,
  children,
}: {
  title?: string;
  parentTitle?: string;
  backTo?: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  size?: 'column' | 'wide';
  padded?: boolean;
  children: React.ReactNode;
}) => {
  const { isOverlay } = useSidebar();

  const isDrillIn = parentTitle != null;
  const showLargeTitle = title != null && !isOverlay;

  let titleBlock: React.ReactNode = null;
  if (isDrillIn) {
    titleBlock =
      backTo != null || title != null ? (
        <Stack gap={2} align="start">
          {backTo != null && <SettingsBackLink to={backTo} />}
          {title != null && (
            <Heading size="2xl" weight="medium" level={1}>
              {title}
            </Heading>
          )}
        </Stack>
      ) : null;
  } else if (showLargeTitle || actions != null) {
    titleBlock = (
      <Stack
        direction="horizontal"
        justify={showLargeTitle ? 'between' : 'end'}
        align="center"
        gap={4}
      >
        {showLargeTitle && (
          <Heading size="4xl" weight="medium" level={1}>
            {title}
          </Heading>
        )}
        {actions}
      </Stack>
    );
  }

  let pageHeader: React.ReactNode = titleBlock;
  if (description != null) {
    pageHeader = (
      <Stack gap={2} align="stretch">
        {titleBlock}
        <Text size="sm" color="tertiary">
          {description}
        </Text>
      </Stack>
    );
  }

  const documentTitle = title ?? parentTitle;

  return (
    <Main>
      {documentTitle != null && (
        <Helmet>
          <title>{`${documentTitle} | Eluve`}</title>
        </Helmet>
      )}
      {isOverlay && (
        <Header>
          <SidebarTrigger />
          {(parentTitle ?? title) != null && (
            <Heading size="2xl" responsive weight="medium" level={1}>
              {parentTitle ?? title}
            </Heading>
          )}
        </Header>
      )}
      <Content size={size === 'wide' ? 'xl' : undefined}>
        {padded ? (
          <div className="px-4 py-6 pb-50 sm:px-6 md:px-12 md:pt-10">
            <Stack
              gap={size === 'wide' ? 8 : 12}
              align="stretch"
              className={size === 'wide' ? undefined : 'mx-auto max-w-160'}
            >
              {pageHeader}
              {children}
            </Stack>
          </div>
        ) : (
          children
        )}
      </Content>
    </Main>
  );
};
